import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { usePageTracking } from '@/hooks/usePageTracking';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import StickyWaitlistCTA from '@/components/StickyWaitlistCTA';
import '@/App.css';

// Lazy load pages for code splitting - reduces initial bundle size
const HomePage = lazy(() => import('@/pages/HomePage'));
const InvestmentCalculator = lazy(() => import('@/pages/InvestmentCalculator'));
const InvestmentPlannerV2 = lazy(() => import('@/pages/InvestmentPlannerV2'));
const BudgetDiagnosis = lazy(() => import('@/pages/BudgetDiagnosis'));
const EmergencyFundCalculator = lazy(() => import('@/pages/EmergencyFundCalculator'));
const KPRCalculator = lazy(() => import('@/pages/KPRCalculator'));
const KPRPlanner = lazy(() => import('@/pages/KPRPlanner'));
const InsuranceCalculator = lazy(() => import('@/pages/InsuranceCalculator'));
const NewsPage = lazy(() => import('@/pages/NewsPage'));
const NewsArticlePage = lazy(() => import('@/pages/NewsArticlePage'));
const AdminLogin = lazy(() => import('@/pages/AdminLogin'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const AdminLeads = lazy(() => import('@/pages/AdminLeads'));
const TentangPage = lazy(() => import('@/pages/TentangPage'));
const AuthorPage = lazy(() => import('@/pages/AuthorPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const DisclaimerPage = lazy(() => import('@/pages/DisclaimerPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));

// New scalable calculator system
const CalculatorDirectory = lazy(() => import('@/pages/CalculatorDirectory'));
const CalculatorPage = lazy(() => import('@/pages/CalculatorPage'));
const PremiumPage = lazy(() => import('@/pages/PremiumPage'));

// New directory pages
const KalkulatorDirectory = lazy(() => import('@/pages/KalkulatorDirectory'));
const AIToolsDirectory = lazy(() => import('@/pages/AIToolsDirectory'));
const GuidesDirectory = lazy(() => import('@/pages/GuidesDirectory'));
const GuideArticlePage = lazy(() => import('@/pages/GuideArticlePage'));
const GuidePreviewPage = lazy(() => import('@/pages/GuidePreviewPage'));
const NewsPreviewPage = lazy(() => import('@/pages/NewsPreviewPage'));

// Landing pages
const TemplateExcelWaitlist = lazy(() => import('@/pages/TemplateExcelWaitlist'));

// Search page
const SearchPage = lazy(() => import('@/pages/SearchPage'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
    </div>
  );
}

// Helper components for English to Indonesian redirects
function EnglishCalculatorRedirect() {
  const { category, slug } = useParams();
  return <Navigate to={`/calculators/${category}/${slug}`} replace />;
}

function EnglishBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/news/${slug}`} replace />;
}

// Analytics page tracking component
function PageTracker() {
  usePageTracking();
  return null;
}

function AppLayout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isAdminRoute) {
    return <>{children}</>;
  }
  
  return (
    <>
      <Navbar />
      {children}
      <Footer />
      <StickyWaitlistCTA />
    </>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ============================================ */}
        {/* NEW DIRECTORY PAGES */}
        {/* ============================================ */}
        <Route path="/kalkulator" element={<Navigate to="/calculators" replace />} />
        <Route path="/ai-tools" element={<AIToolsDirectory />} />
        <Route path="/guides" element={<GuidesDirectory />} />
        <Route path="/guides/preview/:id" element={<GuidePreviewPage />} />
        <Route path="/guides/:slug" element={<GuideArticlePage />} />
        
        {/* NEWS ROUTES - TEMPORARILY DISABLED */}
        {/* <Route path="/news" element={<NewsPage />} /> */}
        {/* <Route path="/news/preview/:id" element={<NewsPreviewPage />} /> */}
        {/* <Route path="/news/:slug" element={<NewsArticlePage />} /> */}
        
        {/* Search */}
        <Route path="/search" element={<SearchPage />} />
        
        {/* Blog to News redirects - TEMPORARILY DISABLED */}
        {/* <Route path="/blog" element={<Navigate to="/news" replace />} /> */}
        {/* <Route path="/blog/:slug" element={<EnglishBlogRedirect />} /> */}
        
        {/* ============================================ */}
        {/* LEGACY REDIRECTS - Old URLs to New Structure */}
        {/* ============================================ */}
        <Route path="/investasi" element={<Navigate to="/calculators/investment/compound-interest-calculator" replace />} />
        <Route path="/dana-darurat" element={<Navigate to="/calculators/savings/emergency-fund-calculator" replace />} />
        <Route path="/kpr" element={<Navigate to="/calculators/property/kpr-calculator" replace />} />
        <Route path="/asuransi" element={<Navigate to="/calculators/insurance/life-insurance-calculator" replace />} />
        <Route path="/budget-planner" element={<Navigate to="/ai-tools/budget-planner" replace />} />
        
        {/* English legacy redirects */}
        <Route path="/en/investment-calculator" element={<Navigate to="/en/calculators/investment/compound-interest-calculator" replace />} />
        <Route path="/en/emergency-fund-calculator" element={<Navigate to="/en/calculators/savings/emergency-fund-calculator" replace />} />
        <Route path="/en/mortgage-calculator" element={<Navigate to="/en/calculators/property/kpr-calculator" replace />} />
        <Route path="/en/insurance-calculator" element={<Navigate to="/en/calculators/insurance/life-insurance-calculator" replace />} />
        <Route path="/en/ai-budget-planner" element={<Navigate to="/en/ai-tools/budget-planner" replace />} />
        
        {/* ============================================ */}
        {/* CALCULATOR ROUTES - Using Full-Featured Pages */}
        {/* ============================================ */}
        {/* Calculator Directory */}
        <Route path="/calculators" element={<CalculatorDirectory />} />
        <Route path="/calculators/:category" element={<CalculatorDirectory />} />
        
        {/* Specific Calculator Pages (full-featured existing pages) */}
        <Route path="/calculators/savings/emergency-fund-calculator" element={<EmergencyFundCalculator />} />
        <Route path="/calculators/insurance/life-insurance-calculator" element={<InsuranceCalculator />} />
        
        {/* Dynamic Calculator Page (for new calculators added via config) */}
        <Route path="/calculators/:category/:slug" element={<CalculatorPage />} />
        
        {/* AI Tools */}
        <Route path="/ai-tools/budget-planner" element={<BudgetDiagnosis />} />
        <Route path="/ai-tools/investment-planner" element={<InvestmentPlannerV2 />} />
        <Route path="/ai-tools/kpr-planner" element={<KPRPlanner />} />
        
        {/* Premium Page */}
        <Route path="/premium" element={<PremiumPage />} />
        
        {/* ============================================ */}
        {/* ENGLISH ROUTES - TEMPORARILY DISABLED */}
        {/* All /en/* routes redirect to Indonesian version */}
        {/* Files preserved - to re-enable, restore original routes */}
        {/* ============================================ */}
        <Route path="/en" element={<Navigate to="/" replace />} />
        <Route path="/en/calculators" element={<Navigate to="/calculators" replace />} />
        <Route path="/en/calculators/:category" element={<Navigate to="/calculators" replace />} />
        <Route path="/en/calculators/:category/:slug" element={<EnglishCalculatorRedirect />} />
        <Route path="/en/ai-tools/budget-planner" element={<Navigate to="/ai-tools/budget-planner" replace />} />
        <Route path="/en/premium" element={<Navigate to="/premium" replace />} />
        {/* English news redirects - TEMPORARILY DISABLED */}
        {/* <Route path="/en/blog" element={<Navigate to="/news" replace />} /> */}
        {/* <Route path="/en/blog/:slug" element={<EnglishBlogRedirect />} /> */}
        {/* <Route path="/en/news" element={<Navigate to="/news" replace />} /> */}
        {/* <Route path="/en/news/:slug" element={<EnglishBlogRedirect />} /> */}
        <Route path="/en/about" element={<Navigate to="/tentang" replace />} />
        <Route path="/en/privacy" element={<Navigate to="/privasi" replace />} />
        <Route path="/en/disclaimer" element={<Navigate to="/disclaimer" replace />} />
        <Route path="/en/contact" element={<Navigate to="/kontak" replace />} />
        {/* Catch-all for any other /en/* routes */}
        <Route path="/en/*" element={<Navigate to="/" replace />} />
        
        {/* ============================================ */}
        {/* MAIN ROUTES */}
        {/* ============================================ */}
        {/* Indonesian Routes (default) */}
        <Route path="/" element={<HomePage />} />
        <Route path="/tentang" element={<TentangPage />} />
        <Route path="/author/cakra-gumilar-karna-saputra" element={<AuthorPage />} />
        <Route path="/privasi" element={<PrivacyPolicyPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/kontak" element={<ContactPage />} />
        
        {/* Landing Pages */}
        <Route path="/template-excel" element={<TemplateExcelWaitlist />} />
        
        {/* Admin Routes (language-neutral) */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/leads" element={<AdminLeads />} />
        
        {/* Backward compatible redirects for old analytics routes */}
        <Route path="/admin/page-views" element={<Navigate to="/admin/dashboard?tab=traffic" replace />} />
        <Route path="/admin/event-analytics" element={<Navigate to="/admin/dashboard?tab=events" replace />} />
        
        {/* Catch-all redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <LanguageProvider>
            <PageTracker />
            <AppLayout>
              <AppRoutes />
            </AppLayout>
            <Toaster position="top-center" richColors />
          </LanguageProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;